import { useState, type Dispatch, type FormEvent, type SetStateAction } from "react";
import type { DashboardData, ExpenseInput } from "../../types";
import { api } from "../../lib/api";
import InlineAddSelect from "./InlineAddSelect";
import { cleanName, matchingName } from "../../lib/names";
import { optionsWithLocalCategory, shouldDeferCreate } from "../../lib/offline-associated";

// The entry form's category and subcategory pickers, each able to add a new choice in place. Online,
// a new one is saved to the household list straight away. Offline, it is kept on the expense and
// created on the list during sync before the expense POST.

type EntryOptions = DashboardData["entryOptions"];

/** The categories an entry of this kind can use, and the subcategories under each. */
export function categoryLists(entryOptions: EntryOptions, isIncome: boolean) {
  return {
    categories:
      (isIncome ? entryOptions?.incomeCategories : entryOptions?.categories) ||
      [],
    subcategoriesByCategory: isIncome
      ? entryOptions?.incomeSubcategoriesByCategory
      : entryOptions?.subcategoriesByCategory,
  };
}

export default function EntryCategoryFields({
  input, setInput, field, isIncome, entryOptions, onOptionsSaved, setCategoryError,
}: {
  input: ExpenseInput;
  setInput: Dispatch<SetStateAction<ExpenseInput>>;
  field: (key: keyof ExpenseInput, value: string) => void;
  isIncome: boolean;
  entryOptions: EntryOptions;
  onOptionsSaved: (options: EntryOptions) => void;
  /** Shown by the form, below the row these fields sit in. */
  setCategoryError: (message: string) => void;
}) {
  const lists = categoryLists(entryOptions, isIncome);
  const { subcategoriesByCategory } = lists;
  // Retired choices remain visible while editing historic entries.
  const categories = [...new Set([...lists.categories, ...(input.category ? [input.category] : [])])];
  const subcategories = [...new Set([...(subcategoriesByCategory?.[input.category] || []), ...(input.subcategory ? [input.subcategory] : [])])];
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [addingSubcategory, setAddingSubcategory] = useState(false);
  const [newSubcategoryName, setNewSubcategoryName] = useState("");
  const [categoryBusy, setCategoryBusy] = useState(false);

  function acceptLocal(category: string, subcategory?: string) {
    onOptionsSaved(optionsWithLocalCategory(entryOptions, category, subcategory, isIncome));
  }

  /** Saves the household's category lists with one change, guarded against edits made elsewhere. */
  async function saveOptions(next: Omit<EntryOptions, "subcategories" | "incomeSubcategories">) {
    if (typeof navigator !== "undefined" && !navigator.onLine) return "deferred";
    setCategoryBusy(true);
    setCategoryError("");
    try {
      const result = await api<{
        verified: boolean;
        entryOptions: EntryOptions;
      }>("settings/categories", { ...next, expectedOptions: entryOptions });
      onOptionsSaved(result.entryOptions);
      return "saved";
    } catch (e) {
      if (shouldDeferCreate(e)) return "deferred";
      setCategoryError(
        (e as Error & { status?: number }).status === 409
          ? "Categories changed elsewhere. Please retry."
          : (e as Error).message,
      );
      return "failed";
    } finally {
      setCategoryBusy(false);
    }
  }
  async function createCategory(e: FormEvent) {
    e.preventDefault();
    const typed = cleanName(newCategoryName);
    if (!typed) return;
    // Typing an existing name in another case chooses that category rather than a near-copy of it.
    const existing = matchingName(categories, typed);
    const next = existing || typed;
    if (!existing) {
      const outcome = await saveOptions({
        categories: isIncome
          ? entryOptions.categories
          : [...entryOptions.categories, next].sort(),
        subcategoriesByCategory: entryOptions.subcategoriesByCategory,
        incomeCategories: isIncome
          ? [...entryOptions.incomeCategories, next].sort()
          : entryOptions.incomeCategories,
        incomeSubcategoriesByCategory: entryOptions.incomeSubcategoriesByCategory,
      });
      if (outcome === "failed") return;
      if (outcome === "deferred") acceptLocal(next);
    }
    setInput((v) => ({
      ...v,
      category: next,
      subcategory: "",
      newCategory: existing ? v.newCategory : next,
      newSubcategory: undefined,
    }));
    setAddingCategory(false);
    setNewCategoryName("");
  }
  async function createSubcategory(e: FormEvent) {
    e.preventDefault();
    const typed = cleanName(newSubcategoryName);
    if (!typed || !input.category) return;
    const existing = matchingName(subcategories, typed);
    const next = existing || typed;
    const nextChildren = [
      ...(subcategoriesByCategory?.[input.category] || []),
      next,
    ].sort();
    if (!existing) {
      const outcome = await saveOptions({
        categories: entryOptions.categories,
        subcategoriesByCategory: isIncome
          ? entryOptions.subcategoriesByCategory
          : { ...entryOptions.subcategoriesByCategory, [input.category]: nextChildren },
        incomeCategories: entryOptions.incomeCategories,
        incomeSubcategoriesByCategory: isIncome
          ? { ...entryOptions.incomeSubcategoriesByCategory, [input.category]: nextChildren }
          : entryOptions.incomeSubcategoriesByCategory,
      });
      if (outcome === "failed") return;
      if (outcome === "deferred") acceptLocal(input.category, next);
    }
    setInput((v) => ({
      ...v,
      subcategory: next,
      newSubcategory: existing ? v.newSubcategory : next,
    }));
    setAddingSubcategory(false);
    setNewSubcategoryName("");
  }
  return (
    <>
      <label className={addingCategory ? "field-span-full" : undefined}>
        Category
        <InlineAddSelect
          required
          ariaLabel="Category"
          value={input.category}
          options={categories}
          onSelect={(value) => field("category", value)}
          emptyLabel="Choose category"
          emptyDisabled
          addOptionLabel="+ Add new category…"
          adding={addingCategory}
          onStartAdding={() => { setAddingCategory(true); setCategoryError(""); }}
          name={newCategoryName}
          onName={setNewCategoryName}
          nameLabel="New category name"
          busy={categoryBusy}
          onAdd={createCategory}
          addLabel="Add category"
          onStop={() => { setAddingCategory(false); setNewCategoryName(""); }}
          onCancel={() => { setAddingCategory(false); setNewCategoryName(""); setCategoryError(""); }}
        />
      </label>
      {input.category && input.category !== "Uncategorized" && (
        <label className={addingSubcategory ? "field-span-full" : undefined}>
          <span className="field-label">Subcategory <small>Optional</small></span>
          <InlineAddSelect
            value={input.subcategory}
            options={subcategories}
            onSelect={(value) => field("subcategory", value)}
            emptyLabel={subcategories.length ? "No subcategory" : "No subcategories"}
            addOptionLabel="+ Add new subcategory…"
            adding={addingSubcategory}
            onStartAdding={() => { setAddingSubcategory(true); setCategoryError(""); }}
            name={newSubcategoryName}
            onName={setNewSubcategoryName}
            nameLabel="New subcategory name"
            busy={categoryBusy}
            onAdd={createSubcategory}
            addLabel="Add subcategory"
            onStop={() => { setAddingSubcategory(false); setNewSubcategoryName(""); }}
            onCancel={() => { setAddingSubcategory(false); setNewSubcategoryName(""); setCategoryError(""); }}
          />
        </label>
      )}
    </>
  );
}
